import React, { useState, useEffect } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { ScrollNavbar } from "../../components";
import { Price } from "../../services/searchService";
import { useAuth } from "../../contexts/AuthContext";
import Swal from "sweetalert2";
import {
  bookingService,
  CreateBookingPayload,
} from "../../services/bookingService";
import { paymentService } from "../../services/paymentService";
import config from "../../config/config";
import searchService from "../../services/searchService";
import { VERTICALS, TRIP_TYPES } from "../../constants/tripTypes";
import { CAB_TYPES } from "../../constants/vehicleTypes";
import { FuelType } from "../../constants/fuelTypes";
import { CUSTOMER_TYPES } from "../../constants/customerTypes";
import { PAYMENT_MODES } from "../../constants/paymentTypes";
import { BOOKING_STATUS } from "../../constants/bookingStatus";
import { calculateFare } from "../../utils/fareCalculator";

// --- Helper Functions --- //

const formatPrice = (priceValue: number) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(priceValue);
};

const CabDetails = () => {
  const [price, setPrice] = useState<Price | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();
  const history = useHistory();
  const { id: priceId } = useParams<{ id: string }>();
  const { user } = useAuth();

  const { searchParams } = (location.state as { searchParams: any }) || {};

  useEffect(() => {
    if (!priceId) {
      setError("No price ID provided.");
      setIsLoading(false);
      return;
    }

    const fetchPriceDetails = async () => {
      try {
        setIsLoading(true);
        const response = await searchService.getPriceById(priceId);
        if (response.success && response.data) {
          setPrice(response.data);
        } else {
          throw new Error(response.message || "Could not find cab details.");
        }
      } catch (err: any) {
        setError(err.message);
        Swal.fire("Error", err.message, "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPriceDetails();
  }, [priceId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading cab details...
      </div>
    );
  }

  if (error || !price) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <p className="text-red-500 text-xl mb-4">
          {error || "Could not load cab details."}
        </p>
        <button
          onClick={() => history.push("/dashboard")}
          className="bg-blue-600 text-white py-2 px-4 rounded"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!searchParams) {
    // Only check for searchParams, not for user
    history.replace("/");
    return null;
  }

  const getCarName = () =>
    `${price.vehicle_manufacturer} ${price.vehicle_model}`;

  const fare = calculateFare(
    price,
    searchParams.distanceKm,
    searchParams.pickupTime,
    searchParams.sourceState
  );

  const handlePayment = async () => {
    // 0. Check for authentication
    if (!user) {
      // Save the current path to redirect back after login
      localStorage.setItem(
        "redirect_path",
        location.pathname + location.search
      );
      Swal.fire({
        title: "Login Required",
        text: "You need to be logged in to book a cab.",
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Go to Login",
      }).then((result) => {
        if (result.isConfirmed) {
          history.push("/login");
        }
      });
      return;
    }

    // 1. Create the booking payload
    const bookingPayload: CreateBookingPayload = {
      booking_vertical: VERTICALS.INTERCITY, // Assuming 'intercity' for now
      booking_trip_type: searchParams.returnDate
        ? TRIP_TYPES.ROUNDTRIP
        : TRIP_TYPES.ONEWAY,
      customer_type: CUSTOMER_TYPES.B2C,
      cab_type: CAB_TYPES.SEDAN, // This should be dynamic based on car details
      cab_model: price.vehicle_model,
      cab_fuel_type: price.fuel_type as FuelType,
      pickup_date: searchParams.departureDate,
      pickup_time: searchParams.pickupTime,
      pickup_address: searchParams.sourceCity,
      return_date: searchParams.returnDate,
      return_time: searchParams.dropTime,
      drop_address: searchParams.destinationCity,
      source: price.Plan.source, // Requires actual city UUID
      destination: price.Plan.destination, // Requires actual city UUID
      basefare_bt: price.fixed_fare, // per-km rate
      basekms_bt: fare.includedKm,
      driverallowances_bt: fare.driverCharges,
      drivernightallowances_bt: fare.nightCharges,
      estimated_fare_bt: fare.baseFare + fare.extraFare + fare.driverCharges + fare.nightCharges,
      extrakm_rate: price.extra_fare,
      extrahr_rate: 50, // Placeholder - no per-hour rate field exists on Plan/Price
      tollcharges_bt: fare.tollCharges,
      permitcharges: 0,
      parkingcharges: fare.parkingCharges,
      discount_amount: 0,
      total_fare_before_tax: fare.totalFareBeforeTax,
      sgst_bt: fare.sgst,
      cgst_bt: fare.cgst,
      igst_bt: fare.igst,
      payment_mode: PAYMENT_MODES.ONLINE,
      booking_status: BOOKING_STATUS.PENDING,
    };

    try {
      // 2. Create the booking in your backend
      const bookingRes = await bookingService.createBooking(
        user.id.toString(),
        bookingPayload
      );
      if (!bookingRes.success || !bookingRes.data) {
        throw new Error(bookingRes.message || "Failed to create booking.");
      }

      const bookingId = bookingRes.data.booking_id;

      // 3. Create a payment order with your backend
      const orderRes = await paymentService.createOrder({
        booking_id: bookingId,
        customer_email: user.email,
        customer_phone: user.phone,
      });
      if (!orderRes.success || !orderRes.data) {
        throw new Error(orderRes.message || "Failed to create payment order.");
      }

      const { order_id, amount, currency, key_id, payment_id } = orderRes.data;

      // 4. Open Razorpay Checkout
      const options = {
        key: key_id,
        amount,
        currency,
        name: "FusionCab",
        description: `Booking for ${getCarName()}`,
        order_id,
        handler: async (response: any) => {
          // 5. Verify the payment
          const verifyPayload = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            payment_id,
            booking_id: bookingId,
          };

          try {
            const verifyRes = await paymentService.verifyPayment(verifyPayload);
            if (verifyRes.success) {
              Swal.fire(
                "Payment Successful!",
                "Your booking is confirmed.",
                "success"
              );
              // Redirect to booking details page instead of dashboard
              history.push(`/bookings/${bookingId}`);
            } else {
              throw new Error(
                verifyRes.message || "Payment verification failed."
              );
            }
          } catch (err) {
            Swal.fire("Verification Failed", (err as Error).message, "error");
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.phone,
        },
        theme: {
          color: "#3399cc",
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (error) {
      Swal.fire("Booking Failed", (error as Error).message, "error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <ScrollNavbar />
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left side - Cab Details */}
          <div className="lg:col-span-2 bg-white p-8 rounded-lg shadow-md">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
              {getCarName()}
            </h1>
            <p className="text-md text-gray-500 mb-6">
              or similar {price.fuel_type} vehicle
            </p>

            <div className="mb-8">
              <img
                src="/api/placeholder/600/400" // Replace with actual image if available
                alt={getCarName()}
                className="w-full h-auto object-cover rounded-lg"
              />
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Trip Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700 mb-8">
              <p>
                <strong>From:</strong> {searchParams.sourceCity}
              </p>
              <p>
                <strong>To:</strong> {searchParams.destinationCity}
              </p>
              <p>
                <strong>Pickup Date:</strong>{" "}
                {new Date(searchParams.departureDate).toLocaleDateString()}
              </p>
              <p>
                <strong>Pickup Time:</strong> {searchParams.pickupTime}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Inclusions & Exclusions
            </h2>
            <div className="space-y-4">
              <ul className="list-disc list-inside text-green-600">
                {price.Plan.distance && (
                  <li>{price.Plan.distance} KMs included</li>
                )}
                {price.Plan.is_driver && <li>Driver allowance</li>}
                {price.Plan.is_toll && <li>Toll charges included</li>}
                {price.Plan.is_tax && <li>State tax & GST included</li>}
              </ul>
              <ul className="list-disc list-inside text-red-600">
                <li>Extra KMs at {formatPrice(price.extra_fare)}/KM</li>
                {price.Plan.is_waiting && <li>Waiting charges may apply</li>}
                {price.Plan.is_parking && <li>Parking charges may apply</li>}
                {price.Plan.is_night && <li>Night charges may apply</li>}
              </ul>
            </div>
          </div>

          {/* Right side - Payment Panel */}
          <div className="bg-white p-8 rounded-lg shadow-md h-fit">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Fare Details
            </h2>

            <div className="space-y-3 mb-6">
              <div className="text-sm text-gray-500">
                Trip Distance: {fare.distanceKm.toFixed(1)} km
                {fare.isNight && " • Night pickup"}
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Base Fare ({fare.includedKm} km)</span>
                <span>{formatPrice(fare.baseFare)}</span>
              </div>
              {fare.extraFare > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Extra Distance ({fare.extraKm.toFixed(1)} km)</span>
                  <span>{formatPrice(fare.extraFare)}</span>
                </div>
              )}
              {fare.nightCharges > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Night Charges</span>
                  <span>{formatPrice(fare.nightCharges)}</span>
                </div>
              )}
              {fare.tollCharges > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Toll Charges</span>
                  <span>{formatPrice(fare.tollCharges)}</span>
                </div>
              )}
              {fare.parkingCharges > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Parking Charges</span>
                  <span>{formatPrice(fare.parkingCharges)}</span>
                </div>
              )}
              {fare.driverCharges > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Driver Allowance</span>
                  <span>{formatPrice(fare.driverCharges)}</span>
                </div>
              )}
              {fare.waitingCharges > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Waiting Charges</span>
                  <span>{formatPrice(fare.waitingCharges)}</span>
                </div>
              )}
              {(fare.stateTax > 0 || fare.greenTax > 0) && (
                <div className="flex justify-between text-gray-700">
                  <span>State/Green Tax</span>
                  <span>{formatPrice(fare.stateTax + fare.greenTax)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-700">
                <span>
                  {fare.sgst > 0 || fare.cgst > 0 ? "SGST + CGST (5%)" : "IGST (5%)"}
                </span>
                <span>{formatPrice(fare.sgst + fare.cgst + fare.igst)}</span>
              </div>
              <div className="border-t my-2"></div>
              <div className="flex justify-between text-xl font-bold text-gray-900">
                <span>Total Amount</span>
                <span>{formatPrice(fare.totalFare)}</span>
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="coupon"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Coupon Code
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="coupon"
                  className="flex-grow border-gray-300 rounded-l-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter coupon"
                />
                <button className="bg-gray-800 text-white px-4 rounded-r-md hover:bg-black">
                  Apply
                </button>
              </div>
            </div>

            <button
              onClick={handlePayment}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors"
            >
              Make Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CabDetails;
